/**
 * Vuex mutations for the editor module.
 *
 * @module store/modules/editor/mutations
 */
import { MutationTree } from "vuex/types";
import Vue from "vue";
import { EditorState } from "./types";
import _ from "lodash";

const mutations: MutationTree<EditorState> = {
  /**
   * Sets flag to trigger content node update.
   *
   * @param {EditorState} state - Module state
   * @param {boolean} payload - Update flag value
   */
  SET_CHANGE_CONTENT_NODE(state, payload) {
    Vue.set(state, "updateContentNode", payload);
  },

  /**
   * Sets flag to trigger content node refetch.
   *
   * @param {EditorState} state - Module state
   * @param {boolean} isRefetch - Refetch flag value
   */
  SET_REFETCH_CONTENT_NODE(state, isRefetch) {
    Vue.set(state, "refetchContentNodes", isRefetch);
  },

  /**
   * Sets upload success status.
   *
   * @param {EditorState} state - Module state
   * @param {boolean} isSuccess - Success flag value
   */
  SET_UPLOAD_CONTENT_SUCCESS(state, isSuccess) {
    Vue.set(state, "uploadSuccess", isSuccess);
  },

  /**
   * Stores old version of node before update for maker-checker workflow.
   *
   * @param {EditorState} state - Module state
   * @param {Object} payload - Payload object
   * @param {"content"|"trigger"} payload.type - Node type
   * @param {any} payload.oldNode - Old node data with id property
   */
  APPEND_OLD_NODE_BY_TYPE(state, { type, oldNode }: { type: "content" | "trigger"; oldNode: any }) {
    const invalidType = !["content", "trigger"].includes(type);
    if (invalidType) {
      console.error(`Cannot append oldNode. Type: ${type} not valid`);
      return;
    }
    const oldNodeId = _.get(oldNode, "id");
    if (!oldNodeId) {
      return;
    }

    Vue.set(state.oldNodes[type], `[${oldNodeId}]`, oldNode);
  },

  /**
   * Sets busy state for connected nodes analysis.
   *
   * @param {EditorState} state - Module state
   * @param {boolean} busy - Busy flag value
   */
  SET_CONNECTED_NODES_BUSY(state, busy) {
    Vue.set(state.connectedNodes, "busy", busy);
  },

  /**
   * Sets connected nodes analysis data.
   *
   * @param {EditorState} state - Module state
   * @param {Object} data - Connected nodes data
   * @param {string[]} data.intent - Connected intent IDs
   * @param {string[]} data.contents - Connected content node IDs
   * @param {string[]} data.triggers - Connected trigger node IDs
   */
  SET_CONNECTED_NODES_DATA(state, data) {
    Vue.set(state.connectedNodes, "data", data);
  },
};

export default mutations;
